// frontend/src/useCases/serverTableQuery.ts
import type { ColumnFiltersState, SortingState } from "@tanstack/react-table";

/** A multiSelect column's value: the selected option values (incl. the blank sentinel). */
export function multiSelectParam(filters: ColumnFiltersState, id: string): string[] | undefined {
    const entry = filters.find((f) => f.id === id);
    if (!entry || !Array.isArray(entry.value) || entry.value.length === 0) return undefined;
    return entry.value as string[];
}

/** A text column's value: a non-empty trimmed scalar. */
export function textParam(filters: ColumnFiltersState, id: string): string | undefined {
    const entry = filters.find((f) => f.id === id);
    if (!entry || typeof entry.value !== "string") return undefined;
    const trimmed = entry.value.trim();
    return trimmed === "" ? undefined : trimmed;
}

/** A select column's single value. */
export function selectParam(filters: ColumnFiltersState, id: string): string | undefined {
    const entry = filters.find((f) => f.id === id);
    if (!entry || entry.value === undefined || entry.value === null || entry.value === "") {
        return undefined;
    }
    return String(entry.value);
}

/** A boolean column's value. */
export function booleanParam(filters: ColumnFiltersState, id: string): boolean | undefined {
    const entry = filters.find((f) => f.id === id);
    if (!entry || typeof entry.value !== "boolean") return undefined;
    return entry.value;
}

/** A date-range column's value. */
export function dateRangeParam(
    filters: ColumnFiltersState,
    id: string,
): { from?: string; to?: string } | undefined {
    const entry = filters.find((f) => f.id === id);
    if (!entry || typeof entry.value !== "object" || entry.value === null) return undefined;
    const v = entry.value as { from?: string; to?: string };
    if (!v.from && !v.to) return undefined;
    return { from: v.from || undefined, to: v.to || undefined };
}

/** A numberRange column's full value: the from/to range plus the "or select
    values" checkbox selection (see NumberRangeMultiSelectFilterContent). */
export function numberRangeMultiSelectParam(
    filters: ColumnFiltersState,
    id: string,
): { from?: string; to?: string; selected?: string[] } | undefined {
    const entry = filters.find((f) => f.id === id);
    if (!entry || typeof entry.value !== "object" || entry.value === null) return undefined;
    const v = entry.value as { from?: string; to?: string; selected?: string[] };
    const from = v.from?.trim() || undefined;
    const to = v.to?.trim() || undefined;
    const selected = Array.isArray(v.selected) && v.selected.length > 0 ? v.selected : undefined;
    if (from === undefined && to === undefined && selected === undefined) return undefined;
    return { from, to, selected };
}

/** A numberRange column's value: split the DataTable {from,to} shape. */
export function numberRangeParam(
    filters: ColumnFiltersState,
    id: string,
): { min?: string; max?: string } | undefined {
    const entry = filters.find((f) => f.id === id);
    if (!entry || typeof entry.value !== "object" || entry.value === null) return undefined;
    const v = entry.value as { from?: string; to?: string };
    const min = v.from?.trim() || undefined;
    const max = v.to?.trim() || undefined;
    if (min === undefined && max === undefined) return undefined;
    return { min, max };
}

/** Map the first sort entry to backend sort_by/sort_dir params. */
export function sortParam(sorting: SortingState): {
    sort_by?: string;
    sort_dir?: "asc" | "desc";
} {
    const first = sorting[0];
    if (!first) return {};
    return { sort_by: first.id, sort_dir: first.desc ? "desc" : "asc" };
}
